import React from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const AttendanceDayGroup = ({ group, employees }) => {
  const { t, i18n } = useTranslation();
  const getEmployeeName = (id) => {
    const emp = employees.find((e) => e.id === id);
    return emp ? `${emp.last_name} ${emp.first_name}` : t("attComp.empInconnu");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-lg">{group.dateKey}</CardTitle>
              <CardDescription>{t("attComp.dayGroup.recordedCount", { count: group.logs.length })}</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="text-sm">
            {new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(group.date)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("attComp.dayGroup.hour")}</TableHead>
                <TableHead>{t("attComp.dayGroup.colBadgeEmp")}</TableHead>
                <TableHead>{t("attComp.dayGroup.type")}</TableHead>
                <TableHead>{t("attComp.dayGroup.terminal")}</TableHead>
                <TableHead>{t("attComp.dayGroup.verification")}</TableHead>
                <TableHead>{t("attComp.dayGroup.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.logs.map((attendance) => {
                const displayTime = new Date(attendance.timestamp).toLocaleTimeString(i18n.language);
                const badgeOrEmployee = attendance.user_id
                  ? `${t('attComp.dayGroup.badgePrefix')} ${attendance.user_id}`
                  : getEmployeeName(attendance.employee_id);
                const type = attendance.attendance_type === 'clock_in' || attendance.status === 0
                  ? t('attComp.entry') : t('attComp.exit');
                const terminal = attendance.device_sn || attendance.terminal_name || '-';
                const verification = attendance.verification_mode || '-';
                const processStatus = attendance.processed || 'pending';

                return (
                  <TableRow key={attendance.id} className="border-l-4 border-border hover:bg-accent">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        {displayTime}
                      </div>
                    </TableCell>
                    <TableCell>{badgeOrEmployee}</TableCell>
                    <TableCell>
                      <Badge variant={type === t('attComp.entry') ? 'default' : 'secondary'}>{type}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-muted-foreground">{terminal}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground capitalize">{verification}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1">
                        <Clock className="h-3 w-3" />
                        {processStatus}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AttendanceDayGroup;
